import { observer } from 'mobx-react-lite'
import { type ReactNode } from 'react'
import { Button, Icon, IconButton, SpinnerLoader, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { tooltipText } from 'shared/constants/tooltipText'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { useConversationMessagesContext } from 'widgets/ConversationMessages/context'
import styles from './styles.module.scss'

interface RenderContentProps {
  icon: 'inbox' | 'conversation' | 'rcs'
  mainText: string
  secondaryText: string
  actionButton: ReactNode
}

export const EmptyConversationsState = observer(() => {
  const { conversationStore, featureFlagsStore, callStore, inboxesStore, usersStore } =
    useEntitiesContext()
  const store = useConversationMessagesContext()
  const { isActionDisabled } = conversationStore

  if (inboxesStore.localInboxes.hasQuickFilter) return null

  if (store.loadingConversations) {
    return (
      <div className={styles.wrap}>
        <SpinnerLoader ariaLabel={'EmptyConversationsState'} />
      </div>
    )
  }

  const inteliquentCalling = featureFlagsStore.inteliquent_calling
  const canManageInboxCalling = featureFlagsStore.settings_inboxes_manage_inbox_calling_v2
  const inbox = inboxesStore.currentInbox
  const isPFTInbox = inbox?.isPFTInbox
  const isRCS = inbox instanceof Inbox && inbox.isRCS
  const disabledCall =
    !callStore.isShowCall || callStore.loading || (isPFTInbox && !inteliquentCalling)
  const user = usersStore.user
  const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess

  const tooltipCall = () => {
    if (isPFTInbox) return tooltipText.disabledCall(inboxesStore.nonPFTInboxNumberType)
    if (!inbox) return ''
    if (inbox.isUnifiedInbox) return ''
    if (!callStore.isShowCall) {
      if (
        user?.isAdminRole ||
        user?.isOwnerRole ||
        (inbox.type === 'inbox' && inbox.ownerId === user?.id)
      ) {
        return (
          <>
            Outbound calling is disabled
            <button
              onClick={(e) => {
                e.preventDefault()

                const redirectPath = canManageInboxCalling
                  ? `/settings/inboxes/${inbox.id}/settings/calling/general`
                  : `/settings/organization/inboxes/all-inboxes/${inbox.id}/calling`

                uiStore.changeRouteViaVue({
                  path: redirectPath,
                })
              }}
            >
              Enable it here
            </button>
          </>
        )
      }

      return 'Outbound calling is disabled. Contact your admin to enable it.'
    }

    return ''
  }

  const tooltipButton = () => {
    if (isActionDisabled) {
      return 'Disabled; verify toll-free number.'
    }

    return ''
  }

  const renderContent = ({ icon, mainText, secondaryText, actionButton }: RenderContentProps) => (
    <div className={styles.wrap}>
      <div className={styles.icon}>
        <Icon icon={icon} fontSize={24} />
      </div>
      <Typography variant={'body-rg-medium'} className={'mb4'} ariaLabel={'CreateNewConversation'}>
        {mainText}
      </Typography>
      <Typography
        variant={'body-md-regular'}
        className={'tertiary'}
        ariaLabel={'EngageWithYourContacts'}
      >
        {secondaryText}
      </Typography>
      <div className={styles.actions}>{actionButton}</div>
    </div>
  )

  return isShouldRequestAccess
    ? renderContent({
        icon: 'inbox',
        mainText: 'Request inbox access',
        secondaryText: 'Engage with your contacts',
        actionButton: (
          <Button
            icon={'unlock'}
            text={'Request access'}
            loading={usersStore.requestInboxAccessLoading}
            onClick={() => usersStore.fetchRequestInboxAccess()}
          />
        ),
      })
    : renderContent({
        icon: isRCS ? 'rcs' : 'conversation',
        mainText: isRCS ? 'Create new RCS conversation' : 'Create new conversation',
        secondaryText: 'Engage with your contacts',
        actionButton: (
          <>
            <Button
              icon={'add'}
              text={'New message'}
              disabled={isActionDisabled}
              tooltipProps={{
                placement: 'top',
                label: tooltipButton(),
              }}
              onClick={() => conversationStore.conversationOpenStore.handleOpenNewConversation()}
            />
            {!isRCS && (
              <IconButton
                icon={'phone'}
                color={'secondary'}
                onClick={store.callModalStore?.openModal}
                disabled={disabledCall}
                tooltipProps={{
                  placement: 'bottom',
                  label: tooltipCall(),
                }}
                ariaLabel='EmptyConversationsState_tooltipCall'
              />
            )}
          </>
        ),
      })
})
